import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { eachDayOfInterval, format, isWithinInterval, parse } from "date-fns";
import {
  type FormEventHandler,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUserId } from "../../hooks/useUserId";
import {
  PresenceTableName,
  useDynamoDeleteItem,
  useDynamoMutateItem,
} from "../../services/dynamo";
import {
  DayStringFormat,
  MonthStringFormat,
  type DateString,
  type Presence,
  type PresenceDay,
  type PresenceRange,
} from "../../types/presence";
import { useQueryPresencePKSKPrefix } from "../../hooks/data/use-presence-data";
import { toast } from "sonner";
import {
  deleteObjectFromS3,
  getPresignedUploadUrl,
  uploadFileToS3,
} from "../../services/api";
import AttachmentItem from "./AttachmentItem";
import Typography from "../Typography";
import { Upload, UploadIcon } from "lucide-react";

const ACCEPTED_CERT_MIME = "image/*,.pdf,.doc,.docx";

type CertEntry = {
  id: string;
  name: string;
  file: File;
  key?: string;
  uploading: boolean;
  error: boolean;
};

type NoWorkDaysFormProps = {
  date: Date;
  data?: PresenceRange;
  contentToDisplay: Extract<
    PresenceDay["reason"],
    "ferie" | "festività" | "chiusura ufficio" | "malattia" | "maternità"
  >;
  triggerAnnulla?: () => void;
};

export default function NoWorkDaysForm({
  contentToDisplay,
  date,
  data,
  triggerAnnulla,
}: NoWorkDaysFormProps) {
  const userId = useUserId();

  const parsedStart = useMemo(
    () =>
      data?.startDate
        ? parse(data.startDate, DayStringFormat, new Date())
        : undefined,
    [data?.startDate],
  );
  const parsedEnd = useMemo(
    () =>
      data?.endDate
        ? parse(data.endDate, DayStringFormat, new Date())
        : undefined,
    [data?.endDate],
  );

  const [startDate, setStartDate] = useState<Date>(parsedStart ?? date);
  const [endDate, setEndDate] = useState<Date>(parsedEnd ?? date);

  // Query presence data for the months covered by the selected range (used for malattia validation)
  const startMonthStr = format(startDate, MonthStringFormat);
  const endMonthStr = format(endDate, MonthStringFormat);

  const { data: startMonthPresences } = useQueryPresencePKSKPrefix<Presence>(
    `${userId}`,
    `presenceDay#${startMonthStr}`,
  );
  const { data: endMonthPresences } = useQueryPresencePKSKPrefix<Presence>(
    `${userId}`,
    `presenceDay#${endMonthStr}`,
  );

  // Days in other absence types that block the range selection for range absences
  const blockedDates = useMemo(() => {
    if (
      contentToDisplay !== "malattia" &&
      contentToDisplay !== "ferie" &&
      contentToDisplay !== "festività" &&
      contentToDisplay !== "chiusura ufficio" &&
      contentToDisplay !== "maternità"
    ) {
      return [];
    }

    // Combine both months, deduplicate by sk
    const skSeen = new Set<string>();
    const allPresences: Presence[] = [];
    for (const p of [
      ...(startMonthPresences ?? []),
      ...(endMonthPresences ?? []),
    ]) {
      if (!skSeen.has(p.sk)) {
        skSeen.add(p.sk);
        allPresences.push(p);
      }
    }

    // Days belonging to the entry currently being edited should not be counted as blocked
    const currentEditedDays = new Set<string>();
    if (data?.startDate && data?.endDate) {
      for (const d of eachDayOfInterval({
        start: parse(data.startDate, DayStringFormat, new Date()),
        end: parse(data.endDate, DayStringFormat, new Date()),
      })) {
        currentEditedDays.add(format(d, DayStringFormat));
      }
    }

    return allPresences
      .filter((p) => p.reason !== contentToDisplay)
      .filter((p) => !currentEditedDays.has(p.sk.replace("presenceDay#", "")))
      .map((p) =>
        parse(p.sk.replace("presenceDay#", ""), DayStringFormat, new Date()),
      );
  }, [
    contentToDisplay,
    startMonthPresences,
    endMonthPresences,
    data?.startDate,
    data?.endDate,
    data?.reason,
  ]);

  const hasBlockedDays = useMemo(() => {
    if (blockedDates.length === 0) return false;
    const interval = { start: startDate, end: endDate };
    return blockedDates.some((d) => isWithinInterval(d, interval));
  }, [blockedDates, startDate, endDate]);

  // Certificate state (malattia only)
  const [certEntries, setCertEntries] = useState<CertEntry[]>([]);
  const [removedCertKeys, setRemovedCertKeys] = useState<Set<string>>(
    new Set(),
  );
  const certInputRef = useRef<HTMLInputElement>(null);
  const isCertUploading = certEntries.some((e) => e.uploading);

  const savedCerts = (data?.attachments ?? []).filter(
    (k) => !removedCertKeys.has(k),
  );
  const newCertKeys = certEntries.filter((e) => e.key).map((e) => e.key!);

  const { mutateAsync, isPending } = useDynamoMutateItem<PresenceRange>(
    PresenceTableName,
    {},
  );
  const { mutateAsync: deleteAsync, isPending: isPendingDelete } =
    useDynamoDeleteItem(PresenceTableName, {});

  const isLoading = isPending || isPendingDelete;

  const handleCertFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    if (certInputRef.current) certInputRef.current.value = "";
    const year = startDate.getFullYear();
    const month = startDate.getMonth() + 1;
    for (const file of files) {
      const id = crypto.randomUUID();
      setCertEntries((prev) => [
        ...prev,
        { id, name: file.name, file, uploading: true, error: false },
      ]);
      try {
        const { url, key } = await getPresignedUploadUrl({
          year,
          month,
          filename: file.name,
          contentType: file.type || "application/octet-stream",
        });
        await uploadFileToS3(url, file);
        setCertEntries((prev) =>
          prev.map((entry) =>
            entry.id === id ? { ...entry, key, uploading: false } : entry,
          ),
        );
        toast.success(`${file.name} caricato con successo`);
      } catch {
        setCertEntries((prev) =>
          prev.map((entry) =>
            entry.id === id
              ? { ...entry, uploading: false, error: true }
              : entry,
          ),
        );
        toast.error(`Errore nel caricamento di ${file.name}`);
      }
    }
  };

  const handleRemoveCertEntry = async (entry: CertEntry) => {
    setCertEntries((prev) => prev.filter((e) => e.id !== entry.id));
    if (entry.key) {
      try {
        await deleteObjectFromS3(entry.key);
      } catch {
        // silently ignore
      }
    }
  };

  const handleRemoveSavedCert = async (key: string) => {
    setRemovedCertKeys((prev) => new Set([...prev, key]));
    try {
      await deleteObjectFromS3(key);
    } catch {
      // silently ignore
    }
  };

  const handleDelete = useCallback(async () => {
    if (!data) return;
    const daysToDelete = eachDayOfInterval({
      start: parse(data.startDate, DayStringFormat, new Date()),
      end: parse(data.endDate, DayStringFormat, new Date()),
    });
    const toastId = toast.loading(
      `Eliminazione di ${daysToDelete.length} giorni...`,
    );
    try {
      for (const day of daysToDelete) {
        await deleteAsync({
          key: {
            pk: `${userId}`,
            sk: `presenceDay#${format(day, DayStringFormat)}`,
          },
        });
      }
      toast.success("Registrazione eliminata con successo", { id: toastId });
      if (triggerAnnulla) {
        triggerAnnulla();
      }
    } catch {
      toast.error("Errore durante l'eliminazione", { id: toastId });
    }
  }, [data, deleteAsync, userId, triggerAnnulla]);

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault();
      const newInterval = { start: startDate, end: endDate };
      const oldInterval =
        parsedStart && parsedEnd
          ? { start: parsedStart, end: parsedEnd }
          : undefined;

      const newDays = eachDayOfInterval(newInterval);
      const daysToDelete = oldInterval
        ? eachDayOfInterval(oldInterval).filter(
            (d) => !isWithinInterval(d, newInterval),
          )
        : [];

      const toastId = toast.loading(
        `Aggiornamento di ${newDays.length + daysToDelete.length} giorni...`,
      );
      try {
        const currentSavedCerts = (data?.attachments ?? []).filter(
          (k) => !removedCertKeys.has(k),
        );
        const currentNewCertKeys = certEntries
          .filter((e) => e.key)
          .map((e) => e.key!);
        const attachments = [...currentSavedCerts, ...currentNewCertKeys];

        for (const day of newDays) {
          const formatted = format(day, DayStringFormat) as DateString;
          await mutateAsync({
            pk: `${userId}`,
            sk: `presenceDay#${formatted}`,
            reason: contentToDisplay,
            date: formatted,
            hours: 8,
            startDate: format(startDate, DayStringFormat),
            endDate: format(endDate, DayStringFormat),
            createdAt: data?.createdAt ?? Date.now(),
            updatedAt: Date.now(),
            ...(contentToDisplay === "malattia" ? { attachments } : {}),
          });
        }
        for (const day of daysToDelete) {
          await deleteAsync({
            key: {
              pk: `${userId}`,
              sk: `presenceDay#${format(day, DayStringFormat)}`,
            },
          });
        }
        toast.success("Registrazione effettuata con successo", { id: toastId });
        if (triggerAnnulla) {
          triggerAnnulla();
        }
      } catch {
        toast.error("Errore durante il salvataggio", { id: toastId });
      }
    },
    [
      startDate,
      endDate,
      parsedStart,
      parsedEnd,
      mutateAsync,
      deleteAsync,
      userId,
      contentToDisplay,
      data?.attachments,
      data?.createdAt,
      triggerAnnulla,
      removedCertKeys,
      certEntries,
    ],
  );

  const handleStartChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value)
        setStartDate(parse(e.target.value, DayStringFormat, new Date()));
    },
    [],
  );

  const handleEndChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value)
        setEndDate(parse(e.target.value, DayStringFormat, new Date()));
    },
    [],
  );

  return (
    <div className="NoWorkDaysForm">
      {!isLoading && (
        <form id="no-work-days-form" onSubmit={onSubmit}>
          <label className="NoWorkDaysForm__label">
            <Typography variant="body2" className="bold">
              Data inizio
            </Typography>
            <Input
              type="date"
              value={format(startDate, DayStringFormat)}
              onChange={handleStartChange}
              className="w-full"
            />
          </label>
          <label className="NoWorkDaysForm__label">
            <Typography variant="body2" className="bold">
              Data fine
            </Typography>
            <Input
              type="date"
              value={format(endDate, DayStringFormat)}
              onChange={handleEndChange}
              className="w-full"
            />
          </label>
          {hasBlockedDays && (
            <Typography variant="body2" className="text-destructive">
              L'intervallo selezionato include giorni con un'altra tipologia di
              assenza registrata.
            </Typography>
          )}
        </form>
      )}
      {contentToDisplay === "malattia" && !isLoading && (
        <div className="NoWorkDaysForm__certificates">
          <Typography variant="body2" className="bold">
            Certificato medico:
          </Typography>
          {(savedCerts.length > 0 || certEntries.length > 0) && (
            <ul className="NoWorkDaysForm__cert-list">
              {savedCerts.map((key) => (
                <AttachmentItem
                  key={key}
                  name={key.split("/").pop() ?? key}
                  s3Key={key}
                  onRemove={() => handleRemoveSavedCert(key)}
                />
              ))}
              {certEntries.map((entry) => (
                <AttachmentItem
                  key={entry.id}
                  name={entry.name}
                  file={entry.file}
                  uploading={entry.uploading}
                  error={entry.error}
                  onRemove={
                    !entry.uploading
                      ? () => handleRemoveCertEntry(entry)
                      : undefined
                  }
                />
              ))}
            </ul>
          )}
          {savedCerts.length === 0 && certEntries.length === 0 && (
            <Typography variant="body2">Nessun certificato caricato</Typography>
          )}
          <div className="NoWorkDaysForm__cert-upload">
            <input
              ref={certInputRef}
              type="file"
              multiple
              accept={ACCEPTED_CERT_MIME}
              className="NoWorkDaysForm__cert-input"
              onChange={handleCertFileChange}
            />
            <Button
              type="button"
              variant="outline"
              disabled={isCertUploading}
              onClick={() => certInputRef.current?.click()}
            >
              <UploadIcon className="h-4 w-4 mr-1" />
              {isCertUploading ? "Caricamento..." : "Allega certificato"}
            </Button>
          </div>
        </div>
      )}
      <div className="NoWorkDaysForm__buttons">
        {data && !isLoading && (
          <Button variant="ghost" onClick={handleDelete}>
            Elimina
          </Button>
        )}
        {/* <Button onClick={triggerAnnulla} disabled={isLoading}>
          Annulla
        </Button> */}
        {!isLoading && (
          <Button
            type="submit"
            form="no-work-days-form"
            className="ml-auto"
            disabled={isCertUploading || hasBlockedDays}
          >
            {data ? "Aggiorna" : "Salva"}
          </Button>
        )}
      </div>
    </div>
  );
}
